import appConfig from '../config/config';

/**
 * Generic function to make API requests
 * @param endpoint - The API endpoint (relative to API_URL)
 * @param options - Fetch options (method, headers, body, etc.)
 * @returns Parsed JSON response
 */
export const apiRequest = async (endpoint: string, options: RequestInit = {}) => {
    const url = `${appConfig.API_BASE_URL}${endpoint}`;

    const defaultHeaders = {
        'Content-Type': 'application/json',
    };

    const config = {
        ...options,
        headers: {
            ...defaultHeaders,
            ...options.headers,
        },
    };

    try {
        const response = await fetch(url, config);
        if (!response.ok) {
            throw new Error(`API Error: ${response.statusText}`);
        }
        return await response.json();
    } catch (error) {
        console.error('API Request Failed:', error);
        throw error;
    }
};

export const ContactService = {
    /**
     * Sends contact form data to the backend.
     * Note: Adjust the endpoint to match your specific WordPress contact form plugin (e.g., Contact Form 7).
     * Example: '/contact-form-7/v1/contact-forms/{form_id}/feedback'
     */
    sendMessage: async (data: { name: string; email: string; website?: string; comment: string }) => {
        // Using a generic endpoint for demonstration; replace with actual WP REST API endpoint
        // For standard WP comments: '/wp/v2/comments'
        // For Contact Form 7: '/contact-form-7/v1/contact-forms/YOUR_FORM_ID/feedback'

        // Attempting to post to a generic contact endpoint or comments as fallback
        return apiRequest('/contact/v1/send', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    },
};

export const AboutService = {
    /**
     * Fetches the About Us page content.
     */
    getPageData: async () => {
        // Fetching page with slug 'about-us'
        const response = await apiRequest('/wp/v2/pages?slug=about-us', {
            method: 'GET',
        });
        return response[0]; // WP returns an array
    },

    /**
     * Fetches team members/attorneys.
     * Assumes a custom post type 'team' or similar.
     */
    getTeamMembers: async () => {
        // Replace 'team' with the actual endpoint for attorneys if different
        return apiRequest('/wp/v2/team?per_page=4', {
            method: 'GET',
        });
    },

    /**
     * Fetches testimonials.
     * Assumes a custom post type 'testimonials'.
     */
    getTestimonials: async () => {
        return apiRequest('/wp/v2/testimonials?per_page=3', {
            method: 'GET',
        });
    }
};

const apiService = {
    apiRequest,
    ContactService,
    AboutService,
};

export default apiService;
